import * as t from 'io-ts';

import {DeepReadonly} from '../../libs/shared/helpers/src/typescript/typeHelpers';
import {RConversationIdRef, RMessageUidRef} from '../models/api/apiTypes';
import {DraftCommentModel} from '../models/api/commentModel';
import {ConversationBaseModel} from '../models/api/conversationModel';
import {MessageModel} from '../models/api/messageModel';
import {CellsSortDirectionsEnum} from '../models/api/sortOptionModel';
import {CellsContext} from '../store/panels/cells/context/cellsContext';
import {
  InboxesConversationsParameters,
  InboxesTeammateMessagesParameters,
} from '../store/panels/inboxes/parameters/inboxesParameters';
import {makeConversationDateGetter} from './models/conversationHelpers';
import {makeMessageDateGetter} from './models/messageHelpers';

/*
 * Runtime types.
 */

function makeCellsBoundary<T extends t.Mixed>(idType: T) {
  return t.type({
    date: t.number,
    id: idType,
  });
}

function makeCellsRange<T extends ReturnType<typeof makeCellsBoundary>>(boundaryType: T) {
  return t.type({
    topBoundary: t.union([boundaryType, t.undefined]),
    bottomBoundary: t.union([boundaryType, t.undefined]),
  });
}

export const RCellsConversationsBoundary = makeCellsBoundary(RConversationIdRef);
export const RCellsConversationsRange = makeCellsRange(RCellsConversationsBoundary);

export const RCellsMessagesBoundary = makeCellsBoundary(RMessageUidRef);
export const RCellsMessagesRange = makeCellsRange(RCellsMessagesBoundary);

/*
 * Types.
 */

export interface CellsBoundary<TId> {
  readonly date: number;
  readonly id: TId;
}

export type CellsBoundaryBuilder<TItem, TId> = (item: TItem) => CellsBoundary<TId>;

export interface CellsRange<TId> {
  readonly topBoundary: CellsBoundary<TId> | undefined;
  readonly bottomBoundary: CellsBoundary<TId> | undefined;
}

export interface CellsConversationsBoundary
  extends CellsBoundary<number>,
    DeepReadonly<t.TypeOf<typeof RCellsConversationsBoundary>> {}

export interface CellsConversationsRange
  extends CellsRange<number>,
    DeepReadonly<t.TypeOf<typeof RCellsConversationsRange>> {}

export type CellsConversationsBoundaryBuilder = CellsBoundaryBuilder<ConversationBaseModel, number>;

export interface CellsMessagesBoundary
  extends CellsBoundary<string>,
    DeepReadonly<t.TypeOf<typeof RCellsMessagesBoundary>> {}

export interface CellsMessagesRange
  extends CellsRange<string>,
    DeepReadonly<t.TypeOf<typeof RCellsMessagesRange>> {}

export interface CellsMessagesBoundaryBuilder extends CellsBoundaryBuilder<MessageModel, string> {
  (item: MessageModel, commentDraft?: DraftCommentModel): CellsBoundary<string>;
}

/*
 * Helpers.
 */

/** Make the boundary builder for the specified parameters. */
export function makeConversationBoundaryBuilder(
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
): CellsConversationsBoundaryBuilder {
  const dateGetter = makeConversationDateGetter(cellsContext, parameters);

  return (conversation) => ({
    date: dateGetter(conversation),
    id: conversation.id,
  });
}

/** Make the boundary builder for the specified parameters. */
export function makeMessageBoundaryBuilder(
  parameters: InboxesTeammateMessagesParameters,
): CellsMessagesBoundaryBuilder {
  const dateGetter = makeMessageDateGetter(parameters);

  return (item, commentDraft?: DraftCommentModel) => ({
    date: dateGetter(item, commentDraft),
    id: item.uid,
  });
}

/** Check if a given boundary item is without the bounds of the page. */
export function isWithinBounds<TId>(
  boundaries: CellsRange<TId>,
  sortDirection: CellsSortDirectionsEnum,
  item: CellsBoundary<TId>,
) {
  // Check both the top and bottom boundaries.
  const {topBoundary, bottomBoundary} = boundaries;
  const isWithinTop = compareBoundaries(sortDirection, topBoundary, item) <= 0;
  const isWithinBottom = compareBoundaries(sortDirection, bottomBoundary, item) >= 0;

  return isWithinTop && isWithinBottom;
}

export function compareBoundaries<TId>(
  sortDirection: CellsSortDirectionsEnum,
  a: CellsBoundary<TId> | undefined,
  b: CellsBoundary<TId> | undefined,
): number {
  // If the sort order is descending, we need to invert the results.
  const isDescending = sortDirection === CellsSortDirectionsEnum.DESCENDING;
  const multiplier = isDescending ? -1 : 1;

  if (!a || !b) {
    return 0;
  }

  // Compare by dates first.
  if (a.date > b.date) {
    return multiplier;
  }

  if (a.date < b.date) {
    return -multiplier;
  }

  // If they're equal, compare by ID.
  if (a.id > b.id) {
    return multiplier;
  }

  if (a.id < b.id) {
    return -multiplier;
  }

  // Otherwise, they're actually equal.
  return 0;
}
